import type { ItemStatus, SerializableItem } from '../types';
import { MAX_ITEM_ALIASES, MAX_ITEM_TEXT_LENGTH, MAX_SYNC_PHOTO_BASE64_LENGTH } from './item-values';

export type RemoteItem = SerializableItem & {
  photoBase64?: string;
  deletedAt?: string;
  serverUpdatedAt?: string;
};

const VALID_STATUSES = new Set<ItemStatus>(['needs_name', 'needs_location', 'needs_synonyms', 'ready']);

export function normalizeRemoteItem(value: unknown): RemoteItem | null {
  if (!isRecord(value)) return null;
  const id = cleanString(value.id);
  const updatedAt = validDate(value.updatedAt);
  if (!id || !updatedAt) return null;

  const name = cleanString(value.name);
  const locationNote = cleanString(value.locationNote);
  const deletedAt = validDate(value.deletedAt) || undefined;
  const status = VALID_STATUSES.has(value.status as ItemStatus)
    ? value.status as ItemStatus
    : name ? (locationNote ? 'ready' : 'needs_location') : 'needs_name';

  return {
    id,
    name,
    locationNote,
    locationGroup: cleanString(value.locationGroup),
    photoMimeType: cleanString(value.photoMimeType) || 'image/jpeg',
    synonyms: stringArray(value.synonyms),
    aiSuggestions: stringArray(value.aiSuggestions),
    status,
    createdAt: validDate(value.createdAt) || updatedAt,
    updatedAt,
    pendingSync: false,
    photoBase64: cleanBase64(value.photoBase64),
    deletedAt,
    serverUpdatedAt: validDate(value.serverUpdatedAt) || undefined
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cleanString(value: unknown) {
  return typeof value === 'string' ? value.trim().slice(0, MAX_ITEM_TEXT_LENGTH) : '';
}

function validDate(value: unknown) {
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) return '';
  return value;
}

function stringArray(value: unknown) {
  return Array.isArray(value)
    ? value.flatMap((entry) => typeof entry === 'string' && entry.trim() ? [entry.trim().slice(0, MAX_ITEM_TEXT_LENGTH)] : []).slice(0, MAX_ITEM_ALIASES)
    : [];
}

function cleanBase64(value: unknown) {
  if (typeof value !== 'string' || value.length > MAX_SYNC_PHOTO_BASE64_LENGTH || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) return undefined;
  return value || undefined;
}
